import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  TextInput,
  Select,
  ActionIcon,
  Menu,
  rem,
  Grid,
  Alert,
  Modal,
  Divider,
  Progress,
  ThemeIcon,
  Image,
  Textarea,
  Checkbox,
  RingProgress,
  MultiSelect,
  SegmentedControl,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconCheck,
  IconX,
  IconClock,
  IconEye,
  IconDots,
  IconPlayerPlay,
  IconRefresh,
  IconChevronLeft,
  IconTarget,
  IconPhoto,
  IconVideo,
  IconTag,
  IconThumbUp,
  IconThumbDown,
  IconQuestionMark,
  IconBulb,
  IconMapPin,
  IconUser,
  IconUsers,
  IconCheckbox,
  IconSquare,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  useActivityVerifications,
  useVerifyActivity,
  useTeacherClasses,

} from '../../hooks/useTeacher';
import type { TeacherActivityVerification } from '../../services/teacherService';
import { SmartLoader } from '../../components/common/LoadingStates';
import { renderLocation } from '../../utils/locationUtils';

const ActivityVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<TeacherActivityVerification | null>(null);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);
  const [batchModalOpened, { open: openBatchModal, close: closeBatchModal }] = useDisclosure(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const verifyActivityMutation = useVerifyActivity();

  const {
    data: classes,
  } = useTeacherClasses();

  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
    refetch: refetchActivities,
  } = useActivityVerifications({
    page: 1,
    per_page: 100,
  });

  // Enhanced data fetching using real API
  // const {
  //   data: enhancedActivities,
  //   isLoading: enhancedLoading,
  //   error: enhancedError,
  // } = useActivitiesForVerification({
  //   verified: statusFilter === 'pending' ? false : statusFilter === 'verified' ? true : undefined,
  //   limit: 50,
  // });

  // const {
  //   data: unverifiedCount,
  // } = useUnverifiedActivitiesCount();

  // const verifyDetectionMutation = useVerifyActivityDetection();

  const filteredActivities = activities?.filter(activity => {
    const matchesSearch =
      activity.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.activity_type?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || activity.status === statusFilter;

    return matchesSearch && matchesStatus;
  }) || [];

  const stats = {
    total: activities?.length || 0,
    pending: activities?.filter(a => a.status === 'pending').length || 0,
    verified: activities?.filter(a => a.status === 'verified').length || 0,
    rejected: activities?.filter(a => a.status === 'rejected').length || 0,
    needsReview: activities?.filter(a => a.status === 'needs_review').length || 0,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'green';
      case 'rejected': return 'red';
      case 'needs_review': return 'yellow';
      case 'pending': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <IconCheck size={16} />;
      case 'rejected': return <IconX size={16} />;
      case 'needs_review': return <IconQuestionMark size={16} />;
      case 'pending': return <IconClock size={16} />;
      default: return <IconClock size={16} />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'green';
    if (confidence >= 0.7) return 'yellow';
    return 'red';
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'play': return <IconPlayerPlay size={20} />;
      case 'art': return <IconBulb size={20} />;
      case 'reading': return <IconPhoto size={20} />;
      case 'outdoor': return <IconMapPin size={20} />;
      case 'meal': return <IconUser size={20} />;
      default: return <IconTarget size={20} />;
    }
  };

  const handleActivityClick = (activity: TeacherActivityVerification) => {
    setSelectedActivity(activity);
    setVerificationNotes('');
    setSelectedTags([]);
    openDetailModal();
  };

  const handleVerify = async (activityId: string, status: 'verified' | 'rejected' | 'needs_review') => {
    try {
      await verifyActivityMutation.mutateAsync({
        id: activityId.toString(),
        verified: status === 'verified',
        notes: verificationNotes,
      });

      // Remove from selected if in batch mode
      setSelectedActivities(prev => prev.filter(id => id !== activityId.toString()));

      // Close modal if open
      if (detailModalOpened) {
        closeDetailModal();
      }

      // Refresh activities
      refetchActivities();
    } catch (error) {
      // Error handling: Failed to verify activity
    }
  };

  const handleBatchVerify = async (status: 'verified' | 'rejected' | 'needs_review') => {
    try {
      await Promise.all(
        selectedActivities.map(id =>
          verifyActivityMutation.mutateAsync({
            id: id,
            verified: status === 'verified',
            notes: verificationNotes,
          })
        )
      );

      setSelectedActivities([]);
      closeBatchModal();
      refetchActivities();

      notifications.show({
        title: 'Batch Verification Complete',
        message: `${selectedActivities.length} activities have been ${status}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Some activities failed to verify',
        color: 'red',
      });
    }
  };

  const toggleSelectActivity = (activityId: string) => {
    setSelectedActivities(prev =>
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const selectAll = () => {
    setSelectedActivities(filteredActivities.map(a => a.id));
  };

  const deselectAll = () => {
    setSelectedActivities([]);
  };

  const tagOptions = [
    'creative_play',
    'social_interaction',
    'physical_activity',
    'learning_activity',
    'meal_time',
    'rest_time',
    'outdoor_play',
    'art_craft',
    'music_movement',
    'story_time',
  ];

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Activity Verification</Title>
              <Text size="sm" c="dimmed">
                Review and verify AI-detected student activities
              </Text>
            </div>
          </Group>
          <Group>
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as 'grid' | 'list')}
              data={[
                { label: 'Grid', value: 'grid' },
                { label: 'List', value: 'list' },
              ]}
            />
            <ActionIcon variant="subtle" onClick={() => refetchActivities()}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      {/* Statistics */}
      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total</Text>
          <Text fw={700} size="xl">{stats.total}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Pending</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="blue">{stats.pending}</Text>
            <ThemeIcon color="blue" variant="light" size="sm">
              <IconClock size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Verified</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="green">{stats.verified}</Text>
            <ThemeIcon color="green" variant="light" size="sm">
              <IconCheck size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Rejected</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="red">{stats.rejected}</Text>
            <ThemeIcon color="red" variant="light" size="sm">
              <IconX size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Needs Review</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="yellow">{stats.needsReview}</Text>
            <ThemeIcon color="yellow" variant="light" size="sm">
              <IconQuestionMark size={14} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>Filters</Title>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <TextInput
                placeholder="Search activities..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Select
                placeholder="Filter by status"
                leftSection={<IconFilter size={16} />}
                data={[
                  { value: 'all', label: 'All Activities' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'verified', label: 'Verified' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: 'needs_review', label: 'Needs Review' },
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || 'pending')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Select
                placeholder="Filter by class"
                leftSection={<IconUsers size={16} />}
                data={[
                  { value: '', label: 'All Classes' },
                  ...(classes?.map(cls => ({
                    value: cls.id.toString(),
                    label: cls.name
                  })) || [])
                ]}
                value={selectedClassId?.toString() || ''}
                onChange={(value) => setSelectedClassId(value ? parseInt(value) : null)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              {selectedActivities.length > 0 && (
                <Group gap="xs">
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<IconCheckbox size={16} />}
                    onClick={openBatchModal}
                  >
                    Batch Actions ({selectedActivities.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="subtle"
                    onClick={deselectAll}
                  >
                    Clear
                  </Button>
                </Group>
              )}
              {selectedActivities.length === 0 && filteredActivities.length > 0 && (
                <Button
                  size="sm"
                  variant="subtle"
                  leftSection={<IconSquare size={16} />}
                  onClick={selectAll}
                >
                  Select All
                </Button>
              )}
            </Grid.Col>
          </Grid>
        </Stack>
      </Card>

      {/* Activities List */}
      <SmartLoader
        isLoading={activitiesLoading}
        error={activitiesError as Error | null}
        isEmpty={!filteredActivities.length}
        emptyState={
          <Alert color="blue" title="No activities found">
            {searchQuery || statusFilter !== 'all'
              ? "No activities match your current filters. Try adjusting your search criteria."
              : "No activities are pending verification at this time."
            }
          </Alert>
        }
      >
        {viewMode === 'grid' ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {filteredActivities.map((activity) => (
              <Card
                key={activity.id}
                withBorder
                radius="md"
                style={{ position: 'relative' }}
              >
                {/* Selection Checkbox */}
                <Checkbox
                  checked={selectedActivities.includes(activity.id)}
                  onChange={() => toggleSelectActivity(activity.id)}
                  style={{ position: 'absolute', top: rem(12), left: rem(12), zIndex: 10 }}
                />

                <Stack gap="sm" onClick={() => handleActivityClick(activity)} style={{ cursor: 'pointer' }}>
                  {/* Header */}
                  <Group justify="space-between" align="flex-start" ml={32}>
                    <div>
                      <Group gap="xs">
                        <Avatar size="sm" radius="xl">
                          {activity.student_name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <div>
                          <Text fw={500} size="sm">{activity.student_name}</Text>
                          <Text size="xs" c="dimmed">
                            {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Unknown time'}
                          </Text>
                        </div>
                      </Group>
                    </div>
                    <Badge
                      color={getStatusColor(activity.verification_status || 'pending')}
                      leftSection={getStatusIcon(activity.verification_status || 'pending')}
                      size="sm"
                    >
                      {activity.verification_status?.replace('_', ' ') || 'pending'}
                    </Badge>
                  </Group>

                  {/* Activity Type */}
                  <Group gap="xs">
                    <ThemeIcon variant="light" size="lg">
                      {getActivityIcon(activity.activity_type)}
                    </ThemeIcon>
                    <div style={{ flex: 1 }}>
                      <Text fw={500} tt="capitalize">
                        {activity.activity_type?.replace('_', ' ') || 'unknown'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {renderLocation(activity.location)}
                      </Text>
                    </div>
                  </Group>

                  {/* AI Description */}
                  <Card withBorder p="xs" bg="gray.0">
                    <Text size="sm" lineClamp={2}>
                      {activity.ai_description}
                    </Text>
                  </Card>

                  {/* Confidence Score */}
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">AI Confidence</Text>
                    <Group gap={4}>
                      <Progress
                        value={(activity.confidence || 0) * 100}
                        color={getConfidenceColor(activity.confidence || 0)}
                        size="sm"
                        style={{ width: 80 }}
                      />
                      <Text size="xs" fw={500}>
                        {Math.round((activity.confidence || 0) * 100)}%
                      </Text>
                    </Group>
                  </Group>

                  {/* Media Indicators */}
                  <Group gap="xs">
                    {activity.image_url && (
                      <Badge size="xs" variant="light" leftSection={<IconPhoto size={12} />}>
                        Photo
                      </Badge>
                    )}
                    {activity.video_url && (
                      <Badge size="xs" variant="light" leftSection={<IconVideo size={12} />}>
                        Video
                      </Badge>
                    )}
                    {activity.tags && activity.tags.length > 0 && (
                      <Badge size="xs" variant="light" leftSection={<IconTag size={12} />}>
                        {activity.tags.length} tags
                      </Badge>
                    )}
                  </Group>

                  {/* Quick Actions */}
                  {activity.verification_status === 'pending' && (
                    <Group gap="xs" mt="xs">
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        leftSection={<IconThumbUp size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerify(activity.id, 'verified');
                        }}
                        fullWidth
                      >
                        Verify
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconThumbDown size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerify(activity.id, 'rejected');
                        }}
                        fullWidth
                      >
                        Reject
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : (
          <Stack gap="xs">
            {filteredActivities.map((activity) => (
              <Paper key={activity.id} withBorder p="md" radius="md">
                <Group justify="space-between" align="flex-start">
                  <Group align="flex-start" style={{ flex: 1 }}>
                    <Checkbox
                      checked={selectedActivities.includes(activity.id)}
                      onChange={() => toggleSelectActivity(activity.id)}
                    />
                    <Avatar size="md" radius="xl">
                      {activity.student_name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <Group justify="space-between">
                        <div>
                          <Text fw={500}>{activity.student_name}</Text>
                          <Group gap="xs">
                            <Badge
                              variant="light"
                              leftSection={getActivityIcon(activity.activity_type)}
                            >
                              {activity.activity_type?.replace('_', ' ') || 'unknown'}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              {renderLocation(activity.location)} • {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Unknown time'}
                            </Text>
                          </Group>
                        </div>
                        <Group gap="xs">
                          <RingProgress
                            size={60}
                            thickness={4}
                            sections={[{
                              value: (activity.confidence || 0) * 100,
                              color: getConfidenceColor(activity.confidence || 0)
                            }]}
                            label={
                              <Text size="xs" ta="center" fw={700}>
                                {Math.round((activity.confidence || 0) * 100)}%
                              </Text>
                            }
                          />
                          <Badge
                            color={getStatusColor(activity.verification_status || 'pending')}
                            leftSection={getStatusIcon(activity.verification_status || 'pending')}
                          >
                            {activity.verification_status?.replace('_', ' ') || 'pending'}
                          </Badge>
                        </Group>
                      </Group>
                      <Text size="sm" mt="xs" c="dimmed">
                        {activity.ai_description}
                      </Text>
                      {activity.teacher_notes && (
                        <Alert color="blue" mt="xs" p="xs">
                          <Text size="xs">{activity.teacher_notes}</Text>
                        </Alert>
                      )}
                    </div>
                  </Group>
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="subtle">
                        <IconDots size={18} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => handleActivityClick(activity)}
                      >
                        View Details
                      </Menu.Item>
                      {activity.verification_status === 'pending' && (
                        <>
                          <Menu.Item
                            leftSection={<IconThumbUp style={{ width: rem(14), height: rem(14) }} />}
                            color="green"
                            onClick={() => handleVerify(activity.id, 'verified')}
                          >
                            Verify Activity
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconThumbDown style={{ width: rem(14), height: rem(14) }} />}
                            color="red"
                            onClick={() => handleVerify(activity.id, 'rejected')}
                          >
                            Reject Activity
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconQuestionMark style={{ width: rem(14), height: rem(14) }} />}
                            color="yellow"
                            onClick={() => handleVerify(activity.id, 'needs_review')}
                          >
                            Mark for Review
                          </Menu.Item>
                        </>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </SmartLoader>

      {/* Activity Detail Modal */}
      <Modal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        title="Activity Details"
        size="xl"
      >
        {selectedActivity && (
          <Stack gap="md">
            {/* Student Info */}
            <Group>
              <Avatar size="xl" radius="xl">
                {selectedActivity.student_name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <div>
                <Title order={3}>{selectedActivity.student_name}</Title>
                <Group gap="xs">
                  <Badge
                    variant="light"
                    leftSection={getActivityIcon(selectedActivity.activity_type)}
                    size="lg"
                  >
                    {selectedActivity.activity_type?.replace('_', ' ') || 'unknown'}
                  </Badge>
                  <Badge
                    color={getStatusColor(selectedActivity.verification_status || 'pending')}
                    leftSection={getStatusIcon(selectedActivity.verification_status || 'pending')}
                    size="lg"
                  >
                    {selectedActivity.verification_status?.replace('_', ' ') || 'pending'}
                  </Badge>
                </Group>
              </div>
            </Group>

            <Divider />

            <Grid>
              <Grid.Col span={8}>
                {/* Media Preview */}
                {selectedActivity.image_url && (
                  <Card withBorder mb="md">
                    <Image
                      src={selectedActivity.image_url}
                      alt="Activity"
                      height={300}
                      fit="contain"
                    />
                  </Card>
                )}

                {/* AI Analysis */}
                <Card withBorder>
                  <Title order={5} mb="sm">AI Analysis</Title>
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>Description:</Text>
                      <Text size="sm" style={{ flex: 1, textAlign: 'right' }}>
                        {selectedActivity.ai_description}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>Confidence Score:</Text>
                      <Group gap="xs">
                        <Progress
                          value={(selectedActivity.confidence || 0) * 100}
                          color={getConfidenceColor(selectedActivity.confidence || 0)}
                          size="md"
                          style={{ width: 120 }}
                        />
                        <Text size="sm" fw={500}>
                          {Math.round((selectedActivity.confidence || 0) * 100)}%
                        </Text>
                      </Group>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>Location:</Text>
                      <Text size="sm">
                        {renderLocation(selectedActivity.location)}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>Timestamp:</Text>
                      <Text size="sm">
                        {selectedActivity.timestamp ? new Date(selectedActivity.timestamp).toLocaleString() : 'Unknown time'}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={4}>
                {/* Verification Actions */}
                <Card withBorder>
                  <Title order={5} mb="sm">Verification</Title>
                  <Stack gap="md">
                    <Textarea
                      label="Teacher Notes"
                      placeholder="Add notes about this activity..."
                      value={verificationNotes}
                      onChange={(e) => setVerificationNotes(e.target.value)}
                      rows={3}
                    />

                    <MultiSelect
                      label="Activity Tags"
                      placeholder="Select tags"
                      data={tagOptions.map(tag => ({
                        value: tag,
                        label: tag?.replace('_', ' ') || tag,
                      }))}
                      value={selectedTags}
                      onChange={setSelectedTags}
                    />

                    {selectedActivity.verification_status === 'pending' && (
                      <Stack gap="xs">
                        <Button
                          leftSection={<IconThumbUp size={16} />}
                          color="green"
                          onClick={() => handleVerify(selectedActivity.id, 'verified')}
                          fullWidth
                        >
                          Verify Activity
                        </Button>
                        <Button
                          leftSection={<IconQuestionMark size={16} />}
                          color="yellow"
                          variant="light"
                          onClick={() => handleVerify(selectedActivity.id, 'needs_review')}
                          fullWidth
                        >
                          Needs Further Review
                        </Button>
                        <Button
                          leftSection={<IconThumbDown size={16} />}
                          color="red"
                          variant="light"
                          onClick={() => handleVerify(selectedActivity.id, 'rejected')}
                          fullWidth
                        >
                          Reject Activity
                        </Button>
                      </Stack>
                    )}

                    {selectedActivity.verification_status !== 'pending' && (
                      <Alert
                        color={getStatusColor(selectedActivity.verification_status || 'pending')}
                        icon={getStatusIcon(selectedActivity.verification_status || 'pending')}
                      >
                        This activity has been {selectedActivity.verification_status || 'processed'}
                      </Alert>
                    )}
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Stack>
        )}
      </Modal>

      {/* Batch Actions Modal */}
      <Modal
        opened={batchModalOpened}
        onClose={closeBatchModal}
        title="Batch Verification"
        size="md"
      >
        <Stack gap="md">
          <Alert color="blue" icon={<IconCheckbox size={16} />}>
            You have selected {selectedActivities.length} activities for batch verification.
          </Alert>

          <Textarea
            label="Verification Notes (Optional)"
            placeholder="Add notes for all selected activities..."
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            rows={3}
          />

          <Stack gap="xs">
            <Button
              leftSection={<IconThumbUp size={16} />}
              color="green"
              onClick={() => handleBatchVerify('verified')}
              fullWidth
            >
              Verify All Selected
            </Button>
            <Button
              leftSection={<IconQuestionMark size={16} />}
              color="yellow"
              variant="light"
              onClick={() => handleBatchVerify('needs_review')}
              fullWidth
            >
              Mark All for Review
            </Button>
            <Button
              leftSection={<IconThumbDown size={16} />}
              color="red"
              variant="light"
              onClick={() => handleBatchVerify('rejected')}
              fullWidth
            >
              Reject All Selected
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default ActivityVerificationPage;
